/**
 * External store for live front speaker preview during slider drags.
 * Keyed by feature ID to support multiple simultaneous front speakers.
 * Bypasses Redux so undo history is not polluted with intermediate values.
 */

export interface PreviewFrontSpeaker {
  width: number
  height: number
  cornerRadius: number
  xOffset: number
  yOffset: number
}

let snapshot: Record<string, PreviewFrontSpeaker> | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((l) => l())

/**
 * Set (merge) preview for a specific feature ID.
 * @param id — feature ID
 * @param base — full fallback (from Redux) used when no preview record exists yet
 * @param changes — partial overrides to merge on top
 */
export const setPreviewFrontSpeaker = (
  id: string,
  base: PreviewFrontSpeaker,
  changes: Partial<PreviewFrontSpeaker>,
) => {
  const existing = snapshot?.[id]
  snapshot = {
    ...snapshot,
    [id]: {
      width: changes.width ?? existing?.width ?? base.width,
      height: changes.height ?? existing?.height ?? base.height,
      cornerRadius: changes.cornerRadius ?? existing?.cornerRadius ?? base.cornerRadius,
      xOffset: changes.xOffset ?? existing?.xOffset ?? base.xOffset ?? 0,
      yOffset: changes.yOffset ?? existing?.yOffset ?? base.yOffset,
    },
  }
  notify()
}

export const clearPreviewFrontSpeaker = () => {
  snapshot = null
  notify()
}

export const getPreviewFrontSpeakerSnapshot = (): Record<string, PreviewFrontSpeaker> | null => snapshot

export const subscribePreviewFrontSpeaker = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
